import type { Dispatch } from 'redux';
import type { CompanyData, SelectedCompany } from '@/modules/auth/helpers/CompanyStorage';
import * as auth from '@/modules/auth/redux/AuthRedux';
import { parseUserCompaniesAndRolesPayload, type ParsedUserCompaniesAndRoles } from './permissionResolver';

const toSelectedCompany = (company: CompanyData): SelectedCompany => ({
  company_id: company.company_id,
  company_name: company.company_name,
  company_code: company.company_code,
  company_type: company.company_type,
  roles: (company.roles || []).map((role) => ({
    role_id: role.role_id,
    role_name: role.role_name,
    role_display_name: role.role_display_name,
    role_level: role.role_level || 'company',
    permissions: role.permissions || {},
    is_primary: role.is_primary,
    created_at: role.created_at,
  })),
  selected_at: new Date().toISOString(),
});

export interface ApplyUserCompaniesAndRolesOptions {
  /** Refresh roles on selected company from the same API payload */
  syncSelectedCompanyId?: string;
  /** Auto-select first company when none selected (API order; ignores is_primary) */
  autoSelectFirstCompany?: boolean;
  /**
   * When false, only updates auth.companies / globalRoles / hasGlobalRole.
   * Use for toolbar-scope fetches so list filters do not touch login selectedCompany.
   */
  syncSelectedCompany?: boolean;
}

/**
 * Parse API payload and sync auth.companies, globalRoles, hasGlobalRole in Redux.
 */
export function applyUserCompaniesAndRoles(
  dispatch: Dispatch,
  data: unknown,
  options: ApplyUserCompaniesAndRolesOptions = {}
): ParsedUserCompaniesAndRoles {
  const parsed = parseUserCompaniesAndRolesPayload(data);

  dispatch(auth.actions.setCompanies(parsed.companies));
  dispatch(auth.actions.setGlobalRoles(parsed.globalRoles));
  dispatch(auth.actions.setHasGlobalRole(parsed.hasGlobalRole));

  const shouldSyncSelectedCompany = options.syncSelectedCompany !== false;
  if (shouldSyncSelectedCompany) {
    const syncId = options.syncSelectedCompanyId?.trim();
    if (syncId) {
      const company = parsed.companies.find((c) => String(c.company_id) === syncId);
      if (company) {
        dispatch(auth.actions.setSelectedCompany(toSelectedCompany(company)));
      }
    } else if (options.autoSelectFirstCompany && parsed.companies.length > 0) {
      dispatch(auth.actions.setSelectedCompany(toSelectedCompany(parsed.companies[0])));
    }
  }

  return parsed;
}

export function buildSelectedCompanyFromList(
  companies: CompanyData[],
  companyId: string
): SelectedCompany | null {
  const company = companies.find((c) => String(c.company_id) === String(companyId));
  if (!company) return null;
  return toSelectedCompany(company);
}
